import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { C, WZ_ASPECTS, WZ_HOUSES, WZ_HOUSE_THEMES, WZ_PLANETS, WZ_SIGNS } from "../../../core/constants";
import { localizedPath } from "../../../core/localePaths";
import { APP_FONT_SIZE } from "../../../core/theme";
import { mediaMobile } from "../../../core/breakpoints";
import { fetchDailyHoroscope, fetchPlanetaryPositions } from "../../dashboard/api";
import { createWesternGeneratePrediction, fetchPredictionById, mergePredictionResultSnapshot } from "../../predictions/api";
import { toPredictionAccessPopup } from "../../predictions/accessError";
import { getFreeQuotaPopupCopy, shouldShowFreeQuotaPopup } from "../../predictions/freeQuota";
import { createPdfFileName, printHtmlAsPdf } from "../../export/pdfUtils";
import { buildWesternAstrologyPdfHtml } from "../../export/westernAstrologyPdf";
import Button from "../../../ui/Button";
import CityPicker from "../../../ui/CityPicker";
import FreeQuotaConfirmPopup from "../../../ui/FreeQuotaConfirmPopup";
import GlassCard from "../../../ui/GlassCard";
import PredictionAccessPopup from "../../../ui/PredictionAccessPopup";
import { computeWesternChart } from "./utils";
import {
  WESTERN_ELEMENT_COMPAT,
  resolveWesternSignProfile,
} from "./constants";
import { writeBirthProfile } from "../../profile/storage";
import { applyPredictionUserUpdate } from "../../predictions/postSuccessUserUpdate";
import {
  buildFormInputStyle,
  FORM_FIELD_CELL_CLASS,
  formFieldCellStyle,
  formFieldGridClassName,
  formFieldGridStyle,
} from "../../../core/formFields";
import NativePickerInput from "../../../core/NativePickerInput";
import { useCanHover, useIsMobile } from "../../../hooks/useMediaQuery";

function todayIsoDate() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeIsoDate(value) {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function westernChartSummaryFromComputed(chart) {
  if (!chart) return null;
  const signName = (idx) => WZ_SIGNS[idx]?.n || "";
  return {
    sun_sign: signName(chart.sunIdx),
    moon_sign: signName(chart.moonIdx),
    rising_sign: signName(chart.risingIdx),
    dob: chart.dob,
  };
}

function NatalWheel({ chart, acc, selectedWheelItem, onSelectWheelItem }) {
  const cx=200, cy=200, r=176, rInner=138, rMid=154, rPlanet=108, rAspect=78;
  const toRad = deg => (deg-90)*Math.PI/180;
  const pt = (radius,deg) => [cx+radius*Math.cos(toRad(deg)), cy+radius*Math.sin(toRad(deg))];
  return (
    <svg viewBox="0 0 400 400" width="100%" style={{maxWidth:380,margin:"0 auto",display:"block"}}>
      <defs>
        <radialGradient id="nwglow"><stop offset="0%" stopColor={acc.primary} stopOpacity="0.18"/><stop offset="100%" stopColor={acc.primary} stopOpacity="0"/></radialGradient>
        <filter id="pglow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <circle cx={cx} cy={cy} r={r+12} fill="url(#nwglow)"/>
      {Array.from({length:12}).map((_,i) => {
        const s=i*30, e=(i+1)*30;
        const [x1,y1]=pt(rInner,s),[x2,y2]=pt(r,s),[x3,y3]=pt(r,e),[x4,y4]=pt(rInner,e);
        const active=i===chart.sunIdx;
        const sc=WZ_SIGNS[i].col;
        const [lx,ly]=pt(rMid+10,i*30+15);
        const [nx,ny]=pt(rPlanet+22,i*30+15);
        return (
          <g key={i} onClick={() => onSelectWheelItem?.({ type:"sign", signIdx:i })} style={{ cursor:"pointer" }}>
            <path d={`M${x1},${y1} L${x2},${y2} A${r},${r} 0 0,1 ${x3},${y3} L${x4},${y4} A${rInner},${rInner} 0 0,0 ${x1},${y1}`}
              fill={active?`${sc}28`:"rgba(255,255,255,0.018)"} stroke={active?`${sc}77`:"rgba(255,255,255,0.08)"} strokeWidth="0.6"/>
            <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="14" fill={sc} opacity={active?1:0.45}>{WZ_SIGNS[i].s}</text>
            <text x={nx} y={ny} textAnchor="middle" dominantBaseline="middle" fontSize="8.5" fill="rgba(255,255,255,0.18)">{WZ_HOUSES[i]}</text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="1"/>
      <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
      {Array.from({length:12}).map((_,i) => {
        const [x1,y1]=pt(rAspect,i*30),[x2,y2]=pt(rInner,i*30);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>;
      })}
      {chart.aspectList.map((a,i) => {
        const [x1,y1]=pt(rAspect,chart.planetDegs[a.p1]);
        const [x2,y2]=pt(rAspect,chart.planetDegs[a.p2]);
        const isSelected = selectedWheelItem?.type === "aspect" && selectedWheelItem?.index === i;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={a.col} strokeWidth={isSelected ? "1.8" : "0.9"} opacity={isSelected ? "0.95" : "0.55"} strokeDasharray={a.deg===90?"4,3":a.deg===180?"6,3":undefined} onClick={() => onSelectWheelItem?.({ type:"aspect", index:i })} style={{ cursor:"pointer" }}/>;
      })}
      <circle
        cx={cx}
        cy={cy}
        r={rAspect}
        fill="rgba(5,8,24,0.65)"
        stroke="rgba(255,255,255,0.07)"
        strokeWidth="0.5"
        style={{ pointerEvents: "none" }}
      />
      {(() => { const [x1,y1]=pt(rAspect,chart.risingIdx*30),[x2,y2]=pt(r,chart.risingIdx*30); return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={acc.secondary} strokeWidth="1.5" opacity="0.85"/>; })()}
      {chart.planetDegs.map((deg,i) => {
        const [px,py]=pt(rPlanet,deg); const p=WZ_PLANETS[i];
        const isSelected = selectedWheelItem?.type === "planet" && selectedWheelItem?.index === i;
        return (
          <g key={i} filter="url(#pglow)" onClick={() => onSelectWheelItem?.({ type:"planet", index:i })} style={{ cursor:"pointer" }}>
            <circle cx={px} cy={py} r={isSelected ? "13" : "11"} fill={`${p.col}25`} stroke={`${p.col}88`} strokeWidth={isSelected ? "1.8" : "1"}/>
            <text x={px} y={py} textAnchor="middle" dominantBaseline="middle" fontSize="12" fill={p.col}>{p.sym}</text>
          </g>
        );
      })}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="20" fill={acc.primary} opacity="0.6">✦</text>
    </svg>
  );
}

export default function WesternAstrologyPage({ acc, t, locale, getBirthProfileDefaults, authUser, onCreditsUpdate, replayPredictionId, onResetPage }) {
  const fs = APP_FONT_SIZE;
  const westernCreditCostRaw = Number(authUser?.predictionCosts?.western);
  const westernCreditCost = Number.isFinite(westernCreditCostRaw) && westernCreditCostRaw > 0 ? westernCreditCostRaw : null;
  const [dob, setDob] = useState(() => getBirthProfileDefaults(authUser).birthDate);
  const [birthTime, setBirthTime] = useState(() => getBirthProfileDefaults(authUser).birthTime);
  const [birthPlace, setBirthPlace] = useState(() => getBirthProfileDefaults(authUser).birthPlace);
  const [chart, setChart] = useState(null);
  const [planetaryData, setPlanetaryData] = useState(null);
  const [planetaryLoading, setPlanetaryLoading] = useState(false);
  const [planetaryError, setPlanetaryError] = useState("");
  const [animating, setAnimating] = useState(false);
  const [activeTab, setActiveTab] = useState("daily");
  const [readingDate, setReadingDate] = useState(() => todayIsoDate());
  const [dailyReading, setDailyReading] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState("");
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [selectedSignModal, setSelectedSignModal] = useState(null);
  const [selectedWheelItem, setSelectedWheelItem] = useState(null);
  const [showAspectsGuide, setShowAspectsGuide] = useState(false);
  const [selectedHouseIndex, setSelectedHouseIndex] = useState(null);
  const [selectedPlanetKey, setSelectedPlanetKey] = useState(null);
  const [hoveredAllSignIndex, setHoveredAllSignIndex] = useState(null);
  const [showPlanetDetailsMobile, setShowPlanetDetailsMobile] = useState(false);
  const [isMobilePlanetsView, setIsMobilePlanetsView] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(mediaMobile).matches;
  });
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [accessPopupCopy, setAccessPopupCopy] = useState({ title: "", message: "" });
  const [isGenerateLockedByEntitlement, setIsGenerateLockedByEntitlement] = useState(false);
  const [showFreeQuotaPopup, setShowFreeQuotaPopup] = useState(false);
  const [inputError, setInputError] = useState("");
  const requestIdRef = useRef(0);
  const [pendingPredictionResponse, setPendingPredictionResponse] = useState(null);
  const [loadingReplay, setLoadingReplay] = useState(() => Boolean(replayPredictionId));
  const isReplayMode = Boolean(replayPredictionId);
  const isMobile = useIsMobile();
  const canHover = useCanHover();
  const openSignModal = useCallback((payload) => {
    setHoveredAllSignIndex(null);
    setSelectedSignModal(payload);
  }, []);

  const closeSignModal = useCallback(() => {
    setSelectedSignModal(null);
    setHoveredAllSignIndex(null);
  }, []);
  const localizedPowerColor = useMemo(() => {
    const key = String(dailyReading?.power_color_key || "").toLowerCase().trim();
    if (key) return t(`dashboard_daily_power_color_${key}`, dailyReading?.power_color || key);
    return dailyReading?.power_color || "";
  }, [dailyReading, t]);
  const hasDailyReadingForExport = useMemo(() => {
    if (!dailyReading) return false;
    const candidates = [
      dailyReading.daily_reading,
      dailyReading.lucky_number,
      dailyReading.power_color,
      dailyReading.peak_hour,
      dailyReading.love_relationships,
      dailyReading.career_purpose,
      dailyReading.wellness_body,
      dailyReading.affirmation,
    ];
    return candidates.some((value) => String(value ?? "").trim().length > 0);
  }, [dailyReading]);
  const interpolate = useCallback((template, vars = {}) => {
    return Object.entries(vars).reduce((text, [key, value]) => {
      return text.replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
    }, template);
  }, []);
  const generateButtonLabel = westernCreditCost
    ? interpolate(t("wz_generate_chart_cost_dynamic"), { credits: westernCreditCost })
    : t("wz_generate_chart");
  const localizedSignName = useCallback((name) => t(`wz_sign_${String(name || "").toLowerCase()}`, name), [t]);
  const localizedElement = useCallback((element) => t(`wz_element_${String(element || "").toLowerCase()}`, element), [t]);
  const localizedModality = useCallback((modality) => t(`wz_modality_${String(modality || "").toLowerCase()}`, modality), [t]);
  const localizedPlanetName = useCallback((name) => t(`wz_planet_${String(name || "").toLowerCase()}`, name), [t]);
  const ELEMENT_COMPAT = WESTERN_ELEMENT_COMPAT;
  const dailyMood = useMemo(() => {
    const aspects = dailyReading?.key_aspects || [];
    if (!aspects.length) return "balanced";
    const supportive = aspects.filter((item) => String(item.tone) === "supportive").length;
    const challenging = aspects.filter((item) => String(item.tone) === "challenging").length;
    if (supportive > challenging) return "supportive";
    if (challenging > supportive) return "challenging";
    return "balanced";
  }, [dailyReading]);
  const dailyMoodStyle = useMemo(() => {
    const styles = {
      supportive: {
        gradient: `linear-gradient(135deg, ${acc.secondary}28, ${acc.primary}16, rgba(255,255,255,0.03))`,
        chipBg: `${acc.secondary}20`,
        chipBorder: `${acc.secondary}66`,
        chipText: acc.secondary,
        edge: `${acc.secondary}55`,
      },
      challenging: {
        gradient: "linear-gradient(135deg, rgba(255,136,112,0.24), rgba(255,118,117,0.16), rgba(255,255,255,0.03))",
        chipBg: "rgba(255,118,117,0.20)",
        chipBorder: "rgba(255,118,117,0.66)",
        chipText: "#ff9b92",
        edge: "rgba(255,118,117,0.55)",
      },
      balanced: {
        gradient: `linear-gradient(135deg, ${acc.primary}22, ${acc.tertiary}16, rgba(255,255,255,0.03))`,
        chipBg: `${acc.primary}20`,
        chipBorder: `${acc.primary}66`,
        chipText: acc.primary,
        edge: `${acc.primary}55`,
      },
    };
    return styles[dailyMood] || styles.balanced;
  }, [dailyMood, acc]);
  const dailyEnergyIndex = useMemo(() => {
    const energyByMood = { supportive: 82, balanced: 64, challenging: 48 };
    const hasKeyAspects = !!dailyReading?.key_aspects?.length;
    if (!hasKeyAspects) return null;
    return energyByMood[dailyMood] ?? null;
  }, [dailyReading, dailyMood]);

  useEffect(() => {
    try {
      writeBirthProfile({
        birthDate: dob,
        birthTime,
        birthPlace,
      });
    } catch (_err) {
      // Ignore storage write failures in private or restricted contexts.
    }
  }, [dob, birthTime, birthPlace]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia(mediaMobile);
    const sync = () => setIsMobilePlanetsView(media.matches);
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  const loadPlanetaryPositions = useCallback(async ({ dateValue, requestId, forceRefresh }) => {
    if (!dateValue) return;
    setPlanetaryLoading(true);
    setPlanetaryError("");
    try {
      const payload = await fetchPlanetaryPositions({
        locale,
        date: dateValue,
        birthDate: dob,
        birthTime,
        birthPlace,
        forceRefresh,
      });
      if (requestId !== requestIdRef.current) return;
      if (payload?.available) {
        setPlanetaryData(payload);
      } else {
        setPlanetaryData(null);
        setPlanetaryError(t("planetary_unavailable"));
      }
    } catch (_err) {
      if (requestId !== requestIdRef.current) return;
      setPlanetaryData(null);
      setPlanetaryError(_err?.message || t("planetary_unavailable"));
    } finally {
      if (requestId === requestIdRef.current) {
        setPlanetaryLoading(false);
      }
    }
  }, [birthPlace, birthTime, dob, locale, t]);

  const loadDailyReading = useCallback(async ({ dateValue, requestId, forceRefresh }) => {
    if (!dateValue) return null;
    setDailyLoading(true);
    setDailyError("");
    try {
      const payload = await fetchDailyHoroscope({
        locale,
        date: dateValue,
        birthDate: dob,
        birthTime,
        birthPlace,
        forceRefresh,
      });
      if (requestId !== requestIdRef.current) return null;
      if (payload?.available) {
        const sessionDate = normalizeIsoDate(dateValue) || normalizeIsoDate(payload.date);
        const apiDate = normalizeIsoDate(payload.date);
        if (sessionDate && apiDate && sessionDate !== apiDate && !forceRefresh) {
          return loadDailyReading({ dateValue: sessionDate, requestId, forceRefresh: true });
        }
        setDailyReading(sessionDate ? { ...payload, date: sessionDate } : payload);
        if (sessionDate) setReadingDate(sessionDate);
        return payload;
      }

      setDailyReading(payload || null);
      setDailyError(t("dashboard_daily_unavailable"));
      return payload?.available === false ? payload : null;
    } catch (error) {
      if (requestId !== requestIdRef.current) return null;
      setDailyReading(null);
      setDailyError(error?.message || t("dashboard_daily_unavailable"));
      return null;
    } finally {
      if (requestId === requestIdRef.current) setDailyLoading(false);
    }
  }, [birthPlace, birthTime, dob, locale, t]);

  useEffect(() => {
    if (!chart || animating || isReplayMode || !readingDate) return;
    const requestId = requestIdRef.current;
    loadDailyReading({ dateValue: readingDate, requestId, forceRefresh: true });
  }, [locale, chart, animating, isReplayMode, readingDate, loadDailyReading]);

  useEffect(() => {
    if (!chart || animating || isReplayMode || !readingDate) return;
    const requestId = requestIdRef.current;
    loadPlanetaryPositions({ dateValue: readingDate, requestId, forceRefresh: true });
  }, [locale, chart, animating, isReplayMode, readingDate, loadPlanetaryPositions]);

  useEffect(() => {
    if (!replayPredictionId) {
      setLoadingReplay(false);
      return;
    }
    let cancelled = false;
    async function loadReplayPrediction() {
      setLoadingReplay(true);
      try {
        const response = await fetchPredictionById(replayPredictionId);
        if (cancelled) return;
        const prediction = response?.prediction;
        if (!prediction || prediction.feature !== "western") return;
        const replayDob = prediction.input_payload?.date || getBirthProfileDefaults(authUser).birthDate;
        const replayBirthTime = prediction.input_payload?.birth_time || "";
        const replayBirthPlace = prediction.input_payload?.birth_place || "";
        const replayBirthIso = normalizeIsoDate(replayDob);
        const savedDailyDate = normalizeIsoDate(prediction.result_payload?.daily_reading?.date);
        const replayForecastDate = normalizeIsoDate(prediction.input_payload?.forecast_date)
          || normalizeIsoDate(prediction.created_at)
          || (savedDailyDate && savedDailyDate !== replayBirthIso ? savedDailyDate : null)
          || todayIsoDate();
        setReadingDate(replayForecastDate);
        setDob(replayDob || "");
        setBirthTime(replayBirthTime || "");
        setBirthPlace(replayBirthPlace || "");
        const localChart = computeWesternChart(replayDob || "");
        setAnimating(false);
        const requestId = Date.now();
        requestIdRef.current = requestId;
        await loadDailyReading({ dateValue: replayForecastDate, requestId, forceRefresh: true });
        await loadPlanetaryPositions({ dateValue: replayForecastDate, requestId, forceRefresh: true });
        setChart(localChart);
      } catch (_) {
        // If replay fetch fails, keep default generate mode UI state.
      } finally {
        if (!cancelled) setLoadingReplay(false);
      }
    }
    loadReplayPrediction();
    return () => {
      cancelled = true;
    };
  }, [replayPredictionId, authUser, getBirthProfileDefaults, loadDailyReading, loadPlanetaryPositions]);

  useEffect(() => {
    if (isReplayMode || !pendingPredictionResponse || animating || !chart || !hasDailyReadingForExport) return;
    applyPredictionUserUpdate({ authUser, predictionResponse: pendingPredictionResponse, onCreditsUpdate });
    setPendingPredictionResponse(null);
  }, [isReplayMode, pendingPredictionResponse, animating, chart, hasDailyReadingForExport, authUser, onCreditsUpdate]);

  useEffect(() => {
    if (isReplayMode || !isGenerateLockedByEntitlement) return;
    if (!shouldShowFreeQuotaPopup(authUser, "western")) {
      setIsGenerateLockedByEntitlement(false);
    }
  }, [authUser, isGenerateLockedByEntitlement, isReplayMode]);

  const validateDobInput = useCallback((value) => {
    const dobValue = String(value || "").trim();
    if (!dobValue) return t("wz_error_required_dob");
    const match = dobValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return t("wz_error_invalid_dob");
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return t("wz_error_invalid_dob");
    }
    const parsed = new Date(Date.UTC(year, month - 1, day));
    const isSameDate = parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
    if (!isSameDate) return t("wz_error_invalid_dob");
    const today = new Date();
    const normalizedInput = new Date(Date.UTC(year, month - 1, day));
    const normalizedToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    if (normalizedInput > normalizedToday) return t("wz_error_future_dob");
    return "";
  }, [t]);

  async function runGenerate() {
    if (animating || isGenerateLockedByEntitlement) return;
    const dobError = validateDobInput(dob);
    if (dobError) {
      setInputError(dobError);
      setShowFreeQuotaPopup(false);
      return;
    }
    setInputError("");
    const requestId = Date.now();
    requestIdRef.current = requestId;
    setChart(null); setAnimating(true);
    setPlanetaryData(null);
    setPlanetaryLoading(false);
    setPlanetaryError("");
    setDailyReading(null);
    setDailyLoading(false);
    setDailyError("");
    setActiveTooltip(null);
    setSelectedSignModal(null);
    setHoveredAllSignIndex(null);
    setSelectedWheelItem(null);
    setShowAspectsGuide(false);
    setSelectedHouseIndex(null);
    setSelectedPlanetKey(null);
    setShowPlanetDetailsMobile(false);
    setActiveTab("daily");
    try {
      const readingDateForSession = todayIsoDate();
      setReadingDate(readingDateForSession);
      const predictionResponse = await createWesternGeneratePrediction({
        input_payload: {
          date: dob,
          forecast_date: readingDateForSession,
          birth_time: birthTime,
          birth_place: birthPlace,
          locale,
        },
        result_payload: {
          generated_from: "western_generate",
          force_refresh: true,
          replay_seed: {
            date: dob,
            forecast_date: readingDateForSession,
            birth_time: birthTime,
            birth_place: birthPlace,
            locale,
          },
        },
      });
      setPendingPredictionResponse(predictionResponse);
      const localChart = computeWesternChart(dob);
      if (requestId !== requestIdRef.current) return;
      setChart(localChart);
      await loadPlanetaryPositions({ dateValue: readingDateForSession, requestId, forceRefresh: true });
      const dailyPayload = await loadDailyReading({ dateValue: readingDateForSession, requestId, forceRefresh: true });
      if (requestId !== requestIdRef.current) return;
      const predictionId = predictionResponse?.prediction?.id;
      // Persist chart summary whenever we have a computed chart (admin replay + detail view).
      // Daily reading is optional: when the horoscope API is unavailable we still save signs/DOB snapshot.
      if (predictionId) {
        const mergePayload = {};
        if (dailyPayload?.available) {
          mergePayload.daily_reading = dailyPayload;
        }
        const chartSummary = westernChartSummaryFromComputed(localChart);
        if (chartSummary) {
          mergePayload.western_chart_summary = chartSummary;
        }
        if (Object.keys(mergePayload).length > 0) {
          try {
            await mergePredictionResultSnapshot(predictionId, mergePayload);
          } catch {
            // Best-effort; replay can refetch daily if missing from DB
          }
        }
      }
      setAnimating(false);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setChart(null);
      setAnimating(false);
      const popupPayload = toPredictionAccessPopup(error, t);
      if (popupPayload.shouldShowPopup) {
        setAccessPopupCopy({ title: popupPayload.title, message: popupPayload.message });
        setShowAccessPopup(true);
        setIsGenerateLockedByEntitlement(true);
      } else {
        setPlanetaryError(error?.message || t("planetary_unavailable"));
      }
      setPlanetaryLoading(false);
      return;
    }
    setIsGenerateLockedByEntitlement(false);
  }

  function generate({ skipFreeQuotaGuard = false } = {}) {
    if (animating || isGenerateLockedByEntitlement) return;
    const dobError = validateDobInput(dob);
    if (dobError) {
      setInputError(dobError);
      return;
    }
    setInputError("");
    if (!isReplayMode && !skipFreeQuotaGuard && shouldShowFreeQuotaPopup(authUser, "western")) {
      setShowFreeQuotaPopup(true);
      return;
    }
    runGenerate();
  }

  function exportWesternReadingToPdf() {
    if(!chart) return;
    const now = new Date();
    const exportFileName = createPdfFileName("western-astrology-reading", now);
    const exportedAt = now.toLocaleString();

    const elementBalance = ["Fire","Earth","Air","Water"].map((el) => ({
      el,
      count: PLANET_SIGN.filter((s) => s.el === el).length,
    }));

    const synthesisText = chartNarratives.overview || interpolate(
      t(
        "wz_chart_overview_summary",
        "With your Sun in %{sun}, Moon in %{moon}, and %{rising} rising, your chart weaves together %{sun_element} drive, %{moon_element} emotions, and %{rising_element} expression into a singular cosmic signature."
      ),
      {
        sun: localizedSignName(sun?.n),
        moon: localizedSignName(moon?.n),
        rising: localizedSignName(rising?.n),
        sun_element: localizedElement(sun?.el),
        moon_element: localizedElement(moon?.el),
        rising_element: localizedElement(rising?.el),
      }
    );
    const planetRows = planetaryRows.map((row) => ({
      planet: row.planetLabel,
      symbol: row.symbol,
      sign: row.signLabel,
      signSymbol: row.signSymbol,
      degree: row.degreeInSign,
      element: row.elementLabel,
      motion: row.retrograde ? t("planetary_retrograde_chip") : t("wz_planet_direct_chip"),
      description: row.description,
    }));
    const usingApiAspectInsights = Boolean(planetaryData?.aspect_insights?.length);
    const aspectRows = (usingApiAspectInsights ? planetaryData.aspect_insights : (activeChart?.aspectList || []).map((aspect) => ({
      planet_a: WZ_PLANETS[aspect.p1]?.name,
      planet_b: WZ_PLANETS[aspect.p2]?.name,
      aspect: aspect.name,
      orb: aspect.orb,
      summary: "",
      practical_tip: "",
    }))).map((item) => {
      const planetA = localizedPlanetName(item.planet_a);
      const planetB = localizedPlanetName(item.planet_b);
      const aspectLabel = t(`wz_aspect_name_${String(item.aspect || "").toLowerCase()}`, item.aspect);
      const summary = item.summary || buildAspectInsightSummary({
        planetA: item.planet_a,
        planetB: item.planet_b,
        aspectName: item.aspect,
        chartPair: !usingApiAspectInsights,
      });
      const practicalTip = item.practical_tip || t(`wz_aspect_tip_${String(item.aspect || "").toLowerCase()}`, t("wz_aspect_tip_default"));
      return {
        ...item,
        planetA,
        planetB,
        aspectLabel,
        orbLabel: `${Number(item.orb || 0).toFixed(1)}°`,
        summary,
        practical_tip: practicalTip,
      };
    });
    const housesDetails = houseRows.map((house) => {
      const housePlanetKeys = new Set(house.planets.map((planet) => String(planet.key || "").toLowerCase()));
      const relatedAspects = aspectRows.filter((aspect) => {
        const p1 = String(aspect.planet_a || "").toLowerCase();
        const p2 = String(aspect.planet_b || "").toLowerCase();
        return housePlanetKeys.has(p1) || housePlanetKeys.has(p2);
      });
      const strongestRelated = [...relatedAspects].sort((left, right) => Number(left.orb || 0) - Number(right.orb || 0))[0];
      const relatedAspectLabel = strongestRelated?.aspectLabel || t("wz_house_related_aspect_none");
      return {
        house: house.houseNumber,
        theme: house.theme,
        sign: localizedSignName(house.sign?.n),
        signSymbol: house.sign?.s,
        cusp: Number(house.cuspDegree || 0).toFixed(2),
        element: localizedElement(house.sign?.el),
        modality: localizedModality(house.sign?.mod),
        planets: house.planets.map((planet) => ({
          symbol: planet.symbol,
          name: planet.name,
          degree: Number(planet.degreeInSign || 0).toFixed(2),
          sign: planet.signLabel,
          retrograde: planet.retrograde,
        })),
        themeInsight: interpolate(t("wz_house_insight_theme"), {
          house_theme: house.theme,
          sign: localizedSignName(house.sign?.n),
          element: localizedElement(house.sign?.el),
          modality: localizedModality(house.sign?.mod),
        }),
        expressionInsight: interpolate(t("wz_house_insight_expression"), {
          planets: house.planets.length ? house.planets.map((planet) => planet.name).join(", ") : t("wz_house_no_planets"),
          aspect: relatedAspectLabel,
        }),
        growthInsight: interpolate(t("wz_house_insight_growth"), {
          modality: localizedModality(house.sign?.mod).toLowerCase(),
        }),
      };
    });
    const wheelData = {
      sunIndex: activeChart.sunIdx,
      risingIndex: activeChart.risingIdx,
      signs: WZ_SIGNS.map((sign) => ({
        symbol: sign.s,
        name: localizedSignName(sign.n),
        color: sign.col,
      })),
      planets: activeChart.planetDegs.map((deg, index) => ({
        degree: deg,
        symbol: WZ_PLANETS[index]?.sym,
        name: localizedPlanetName(WZ_PLANETS[index]?.name),
        color: WZ_PLANETS[index]?.col,
      })),
      aspects: (activeChart.aspectList || []).map((aspect) => ({
        p1: aspect.p1,
        p2: aspect.p2,
        color: aspect.col,
        deg: aspect.deg,
      })),
    };
    const sunSignProfile = sun ? resolveWesternSignProfile(sun.n, signProfilesByKey, t) : null;
    const signMeta = sun ? {
      name: localizedSignName(sun.n),
      glyph: sun.s,
      dates: sunSignProfile.dateRange,
      element: localizedElement(sun.el),
      modality: localizedModality(sun.mod),
      ruler: sunSignProfile.ruler,
    } : null;

    const harmonicSignsMapped = (dailyReading?.harmonic_signs || []).map((name) => {
      const meta = WZ_SIGNS.find((entry) => String(entry.n).toLowerCase() === String(name).toLowerCase());
      return {
        glyph: meta?.s || "",
        name: t(`wz_sign_${String(name).toLowerCase()}`, name),
      };
    });
    const planetaryInfluencesMapped = (dailyReading?.planetary_influences || []).map((item) => ({
      name: item.headline || "",
      influence: item.note || "",
    }));
    const energyByMood = { supportive: 82, balanced: 64, challenging: 48 };
    const hasKeyAspects = !!dailyReading?.key_aspects?.length;
    const dailyEnergy = hasKeyAspects ? (energyByMood[dailyMood] ?? null) : null;
    const dailyForPdf = dailyReading ? {
      today_thread: dailyReading.today_thread || "",
      lucky_number: dailyReading.lucky_number || "",
      power_color: localizedPowerColor || dailyReading.power_color || "",
      peak_hour: dailyReading.peak_hour || "",
      peak_hour_note: dailyReading.peak_hour_note || "",
      daily_reading: dailyReading.daily_reading || "",
      love_relationships: dailyReading.love_relationships || dailyReading.love || "",
      career_purpose: dailyReading.career_purpose || dailyReading.career || "",
      wellness_body: dailyReading.wellness_body || dailyReading.wellbeing || "",
      affirmation: dailyReading.affirmation || "",
      planetary_influences: planetaryInfluencesMapped,
      harmonic_signs: harmonicSignsMapped,
      energy: dailyEnergy,
    } : null;

    const html = buildWesternAstrologyPdfHtml({
      dob,
      birthTime,
      birthPlace,
      exportedAt,
      sun,
      moon,
      rising,
      sign: signMeta,
      daily: dailyForPdf,
      bigThreeInsights: [
        planetaryData?.big_three?.find((entry) => entry.key === "sun")?.summary || t("wz_sun_sign_desc"),
        planetaryData?.big_three?.find((entry) => entry.key === "moon")?.summary || t("wz_moon_sign_desc"),
        planetaryData?.big_three?.find((entry) => entry.key === "rising")?.summary || t("wz_rising_sign_desc"),
      ],
      confidenceNotes: planetaryData?.confidence_notes || [],
      aspectInsights: aspectRows,
      orbExplainer: t("wz_orb_explainer_short"),
      housesDetails,
      synthesisText,
      planetRows,
      elementBalance,
      wheelData,
      labels: {
        report: t("pdf_report_title"),
        title: t("pdf_western_title"),
        eyebrow: t("pdf_western_eyebrow"),
        natalSection: t("pdf_western_natal_section"),
        dailySection: t("pdf_western_daily_section"),
        todayThread: t("dashboard_daily_thread_label"),
        rulerLabel: t("pdf_western_ruler_label"),
        energyIndex: t("pdf_energy_index_label"),
        dob: t("pdf_date_of_birth"),
        exported: t("pdf_exported"),
        sun: t("wz_sun_sign_label"),
        moon: t("wz_moon_sign_label"),
        rising: t("wz_rising_sign_label"),
        synthesis: t("pdf_chart_synthesis"),
        wheel: t("pdf_wheel_title"),
        wheelFallback: t("pdf_wheel_fallback"),
        planetaryPositions: t("wz_planetary_positions_title"),
        aspects: t("wz_planetary_aspects_title"),
        confidenceNotes: t("wz_confidence_notes_title"),
        housesHighlights: t("wz_tab_houses"),
        elementalBalance: t("wz_elemental_balance"),
        theme: t("pdf_theme_label"),
        expression: t("pdf_expression_label"),
        growth: t("pdf_growth_label"),
        planetsInHouse: t("wz_house_planets_in_house"),
        noPlanets: t("wz_house_no_planets"),
        practicalTip: t("pdf_practical_tip_label"),
        interpretedFrom: t("pdf_interpreted_from_label"),
        generatedBy: t("pdf_generated_by"),
        luckyNumber: t("dashboard_daily_lucky_number_label"),
        powerColor: t("dashboard_daily_power_color_label"),
        peakHour: t("dashboard_daily_peak_hour_label"),
        loveLabel: t("dashboard_daily_love_relationships_label"),
        careerLabel: t("dashboard_daily_career_purpose_label"),
        wellnessLabel: t("dashboard_daily_wellness_body_label"),
        harmonicLabel: t("dashboard_daily_harmonic_signs_label"),
        affirmationLabel: t("dashboard_daily_affirmation_label"),
        planetaryInfluencesLabel: t("dashboard_daily_planetary_influences_label"),
      }
    });
    printHtmlAsPdf({ html, title: exportFileName.replace(/\.pdf$/i, "") });
  }

  const signIndexByName = useMemo(() => {
    const map = {};
    WZ_SIGNS.forEach((sign, index) => {
      map[String(sign.n || "").toLowerCase()] = index;
    });
    return map;
  }, []);
  const activeChart = useMemo(() => {
    if (!chart) return chart;
    const natalPositions = planetaryData?.natal_positions || [];
    if (!natalPositions.length) return chart;
    const nextPlanetDegs = WZ_PLANETS.map((planet, index) => {
      const found = natalPositions.find((position) => String(position.planet || "").toLowerCase() === String(planet.name).toLowerCase());
      return found ? Number(found.longitude || 0) : chart.planetDegs[index];
    });
    const sunPosition = natalPositions.find((position) => String(position.planet || "").toLowerCase() === "sun");
    const moonPosition = natalPositions.find((position) => String(position.planet || "").toLowerCase() === "moon");
    const risingSign = planetaryData?.big_three?.find((item) => item.key === "rising")?.sign;
    const ascLongitude = Number(planetaryData?.chart_primitives?.ascendant_longitude);
    const fallbackRisingIdx = Math.floor((Number.isFinite(ascLongitude) ? ascLongitude : chart.risingIdx * 30) / 30) % 12;
    const risingIdxFromSign = signIndexByName[String(risingSign || "").toLowerCase()];
    return {
      ...chart,
      planetDegs: nextPlanetDegs,
      sunIdx: sunPosition?.sign ? signIndexByName[String(sunPosition.sign).toLowerCase()] : chart.sunIdx,
      moonIdx: moonPosition?.sign ? signIndexByName[String(moonPosition.sign).toLowerCase()] : chart.moonIdx,
      risingIdx: Number.isInteger(risingIdxFromSign) ? risingIdxFromSign : fallbackRisingIdx,
    };
  }, [chart, planetaryData, signIndexByName]);
  const sun = activeChart ? WZ_SIGNS[activeChart.sunIdx] : null;
  const moon = activeChart ? WZ_SIGNS[activeChart.moonIdx] : null;
  const rising = activeChart ? WZ_SIGNS[activeChart.risingIdx] : null;
  const bigThreePopupContentByRole = useMemo(() => {
    const fromPrediction = (key, fallbackText) => (
      planetaryData?.big_three?.find((entry) => String(entry.key || "").toLowerCase() === key)?.summary || fallbackText
    );
    return {
      sun: {
        label: t("wz_sun_sign_label"),
        icon: "☉",
        summary: fromPrediction("sun", t("wz_sun_sign_desc")),
      },
      moon: {
        label: t("wz_moon_sign_label"),
        icon: "☽",
        summary: fromPrediction("moon", t("wz_moon_sign_desc")),
      },
      rising: {
        label: t("wz_rising_sign_label"),
        icon: "↑",
        summary: fromPrediction("rising", t("wz_rising_sign_desc")),
      },
    };
  }, [planetaryData, t]);
  const PLANET_SIGN = activeChart ? activeChart.planetDegs.map((deg) => WZ_SIGNS[Math.floor(deg / 30) % 12]) : [];
  const planetaryExplanations = useMemo(() => {
    const map = {};
    (planetaryData?.explanations || []).forEach((item) => {
      map[item.planet] = item.text;
    });
    return map;
  }, [planetaryData]);
  const planetInterpretations = useMemo(() => {
    const raw = planetaryData?.planet_interpretations;
    if (!raw || typeof raw !== "object") return {};
    return Object.entries(raw).reduce((map, [key, value]) => {
      map[String(key || "").toLowerCase()] = String(value || "");
      return map;
    }, {});
  }, [planetaryData]);
  const signProfilesByKey = useMemo(() => {
    const rows = Array.isArray(planetaryData?.sign_profiles) ? planetaryData.sign_profiles : [];
    return rows.reduce((map, profile) => {
      const key = String(profile?.key || "").toLowerCase();
      if (!key) return map;
      map[key] = {
        dateRange: profile?.date_range || "",
        ruler: profile?.ruler || "",
        strength: profile?.strength || "",
        growthEdge: profile?.growth_edge || "",
      };
      return map;
    }, {});
  }, [planetaryData]);
  const chartNarratives = useMemo(() => ({
    overview: planetaryData?.chart_narratives?.overview || "",
    ascendant: planetaryData?.chart_narratives?.ascendant || "",
  }), [planetaryData]);
  const planetaryRows = useMemo(() => {
    if (planetaryData?.planetary_positions?.length) {
      return planetaryData.planetary_positions.map((position) => {
        const key = String(position.planet || "").toLowerCase();
        const planetMeta = WZ_PLANETS.find((item) => item.name.toLowerCase() === key) || {};
        const signName = String(position.sign || "");
        const signMeta = WZ_SIGNS.find((item) => item.n.toLowerCase() === signName.toLowerCase()) || {};
        return {
          key,
          symbol: planetMeta.sym || "●",
          color: planetMeta.col || C.textSecondary,
          planetLabel: localizedPlanetName(position.planet),
          signSymbol: signMeta.s || "",
          signLabel: localizedSignName(signName),
          degreeInSign: Number(position.degree_in_sign || 0),
          elementLabel: localizedElement(signMeta.el),
          retrograde: !!position.retrograde,
          description: position.interpretation || planetaryExplanations[key] || planetInterpretations[key] || t(`wz_interp_${key}`),
          strengths: position.strengths || "",
          challenges: position.challenges || "",
          actionTip: position.action_tip || "",
        };
      });
    }

    return WZ_PLANETS.map((planet, index) => {
      const sign = PLANET_SIGN[index];
      const key = planet.name.toLowerCase();
      return {
        key,
        symbol: planet.sym,
        color: planet.col,
        planetLabel: localizedPlanetName(planet.name),
        signSymbol: sign?.s || "",
        signLabel: localizedSignName(sign?.n),
        degreeInSign: Number((activeChart?.planetDegs?.[index] || 0) % 30),
        elementLabel: localizedElement(sign?.el),
        retrograde: false,
        description: planetInterpretations[key] || t(`wz_interp_${key}`),
        strengths: "",
        challenges: "",
        actionTip: "",
      };
    });
  }, [planetaryData, planetaryExplanations, PLANET_SIGN, activeChart, localizedPlanetName, localizedSignName, localizedElement, planetInterpretations]);
  useEffect(() => {
    if (!planetaryRows.length) {
      setSelectedPlanetKey(null);
      return;
    }
    if (selectedPlanetKey === null) return;
    const stillExists = planetaryRows.some((row) => row.key === selectedPlanetKey);
    if (!stillExists) setSelectedPlanetKey(null);
  }, [planetaryRows, selectedPlanetKey]);
  const selectedPlanetRow = useMemo(() => {
    if (!planetaryRows.length || selectedPlanetKey === null) return null;
    return planetaryRows.find((row) => row.key === selectedPlanetKey) || null;
  }, [planetaryRows, selectedPlanetKey]);
  const planetSupportContent = useMemo(() => {
    const row = selectedPlanetRow;
    if (!row) return null;
    const elementKey = String(row.elementLabel || "").toLowerCase();
    const safeElement = ["fire", "earth", "air", "water"].includes(elementKey) ? elementKey : "air";
    const motionKey = row.retrograde ? "retrograde" : "direct";
    return {
      strengths: row.strengths || t(`wz_planet_support_strengths_${safeElement}`),
      challenges: row.challenges || t(`wz_planet_support_challenges_${safeElement}`),
      actionTip: row.actionTip || t(`wz_planet_support_action_${motionKey}`),
      motionLabel: row.retrograde ? t("planetary_retrograde_chip") : t("wz_planet_direct_chip"),
    };
  }, [selectedPlanetRow, t]);
  const planetarySourceLabel = useMemo(() => {
    const raw = String(planetaryData?.meta?.source_mode || "unknown");
    return t(`planetary_source_mode_${raw}`, raw);
  }, [planetaryData]);
  const planetaryReasonLabel = useMemo(() => {
    const raw = String(planetaryData?.meta?.degraded_reason || "");
    if (!raw) return "";
    return t(`planetary_degraded_reason_${raw}`, raw);
  }, [planetaryData]);
  const houseSystemLabel = useMemo(() => {
    const raw = String(planetaryData?.chart_primitives?.house_system || "equal_house");
    return t(`wz_house_system_${raw}`, raw);
  }, [planetaryData]);
  const houseSourceLabel = useMemo(() => {
    const raw = String(planetaryData?.chart_primitives?.house_source_mode || "fallback_equal_house");
    return t(`wz_house_source_mode_${raw}`, raw);
  }, [planetaryData]);
  const houseSourceReasonLabel = useMemo(() => {
    const raw = String(planetaryData?.chart_primitives?.house_source_reason || "");
    if (!raw) return "";
    return t(`planetary_degraded_reason_${raw}`, raw);
  }, [planetaryData]);
  const houseRows = useMemo(() => {
    const backendCusps = planetaryData?.chart_primitives?.house_cusps || [];
    const backendPlanets = planetaryData?.chart_primitives?.planets_by_house || [];
    if (backendCusps.length === 12) {
      return backendCusps.map((house, index) => {
        const sign = WZ_SIGNS.find((item) => String(item.n || "").toLowerCase() === String(house.sign || "").toLowerCase()) || WZ_SIGNS[index % 12];
        const planetsHere = backendPlanets
          .filter((planet) => Number(planet.house) === Number(house.house))
          .map((planet) => {
            const meta = WZ_PLANETS.find((item) => String(item.name).toLowerCase() === String(planet.planet).toLowerCase()) || {};
            return {
              key: String(planet.planet || "").toLowerCase(),
              symbol: meta.sym || "●",
              color: meta.col || C.textSecondary,
              name: localizedPlanetName(planet.planet),
              signLabel: localizedSignName(planet.sign),
              degreeInSign: Number(planet.degree_in_sign || 0),
              retrograde: !!planet.retrograde,
            };
          });
        return {
          houseNumber: Number(house.house),
          sign,
          cuspDegree: Number(house.degree_in_sign || 0),
          planets: planetsHere,
          theme: t(`wz_house_theme_${Number(house.house)}`, WZ_HOUSE_THEMES[index]),
        };
      });
    }

    if (!activeChart) return [];

    return WZ_HOUSES.map((houseLabel, index) => {
      const signIdx = (activeChart.risingIdx + index) % 12;
      const sign = WZ_SIGNS[signIdx];
      const planetsHere = WZ_PLANETS.filter((_, planetIndex) => Math.floor(activeChart.planetDegs[planetIndex] / 30) % 12 === signIdx).map((planet) => ({
        key: String(planet.name || "").toLowerCase(),
        symbol: planet.sym,
        color: planet.col,
        name: localizedPlanetName(planet.name),
        signLabel: localizedSignName(sign.n),
        degreeInSign: 0,
        retrograde: false,
      }));
      return {
        houseNumber: index + 1,
        sign,
        cuspDegree: 0,
        planets: planetsHere,
        theme: t(`wz_house_theme_${index + 1}`, WZ_HOUSE_THEMES[index]),
      };
    });
  }, [planetaryData, activeChart, localizedPlanetName, localizedSignName]);
  const selectedHouse = useMemo(() => {
    if (selectedHouseIndex === null) return null;
    return houseRows[selectedHouseIndex] || null;
  }, [selectedHouseIndex, houseRows]);
  const selectedHouseInsights = useMemo(() => {
    if (!selectedHouse) return null;
    const housePlanetKeys = new Set(selectedHouse.planets.map((planet) => String(planet.key || "").toLowerCase()));
    const apiRelatedAspects = (planetaryData?.aspect_insights || []).filter((insight) => {
      const p1 = String(insight.planet_a || "").toLowerCase();
      const p2 = String(insight.planet_b || "").toLowerCase();
      return housePlanetKeys.has(p1) || housePlanetKeys.has(p2);
    });
    const fallbackAspects = (activeChart?.aspectList || []).filter((aspect) => {
      const p1 = String(WZ_PLANETS[aspect.p1]?.name || "").toLowerCase();
      const p2 = String(WZ_PLANETS[aspect.p2]?.name || "").toLowerCase();
      return housePlanetKeys.has(p1) || housePlanetKeys.has(p2);
    });
    const strongestAspect = [...(apiRelatedAspects.length ? apiRelatedAspects : fallbackAspects)]
      .sort((left, right) => Number(left.orb) - Number(right.orb))[0];
    const relatedAspectLabel = strongestAspect
      ? t(
        `wz_aspect_name_${String(strongestAspect.aspect || strongestAspect.name || "").toLowerCase()}`,
        strongestAspect.aspect || strongestAspect.name
      )
      : t("wz_house_related_aspect_none");
    const planetList = selectedHouse.planets.length
      ? selectedHouse.planets.map((planet) => planet.name).join(", ")
      : t("wz_house_no_planets");
    return {
      theme: interpolate(t("wz_house_insight_theme"), {
        house_theme: selectedHouse.theme,
        sign: localizedSignName(selectedHouse.sign.n),
        element: localizedElement(selectedHouse.sign.el),
        modality: localizedModality(selectedHouse.sign.mod),
      }),
      expression: interpolate(t("wz_house_insight_expression"), {
        planets: planetList,
        aspect: relatedAspectLabel,
      }),
      growth: interpolate(t("wz_house_insight_growth"), {
        modality: localizedModality(selectedHouse.sign.mod).toLowerCase(),
      }),
    };
  }, [selectedHouse, activeChart, planetaryData, t, interpolate, localizedSignName, localizedElement, localizedModality]);
  const getAspectInsightTemplateKey = useCallback((aspectName) => {
    const normalized = String(aspectName || "").toLowerCase();
    if (["sextile", "trine"].includes(normalized)) return "wz_aspect_insight_template_harmonious";
    if (["square", "opposition"].includes(normalized)) return "wz_aspect_insight_template_challenging";
    return "wz_aspect_insight_template_neutral";
  }, []);
  const buildAspectInsightSummary = useCallback(({ planetA, planetB, aspectName, chartPair = false }) => {
    const aspectKey = String(aspectName || "").toLowerCase();
    const aspectLabel = t(`wz_aspect_name_${aspectKey}`, aspectName);
    const nameA = localizedPlanetName(planetA);
    const nameB = localizedPlanetName(planetB);
    const keyA = String(planetA || "").toLowerCase();
    const keyB = String(planetB || "").toLowerCase();

    if (keyA && keyA === keyB) {
      return interpolate(t("wz_aspect_insight_same_body"), { planet: nameA });
    }
    if (chartPair) {
      return interpolate(t("wz_aspect_insight_chart_pair"), {
        planet_a: nameA,
        planet_b: nameB,
        aspect: aspectLabel,
      });
    }
    return interpolate(t(getAspectInsightTemplateKey(aspectName), t("wz_aspect_insight_template")), {
      planet_a: nameA,
      planet_b: nameB,
      aspect: aspectLabel,
    });
  }, [t, interpolate, localizedPlanetName, getAspectInsightTemplateKey]);
  useEffect(() => {
    if (selectedHouseIndex === null) return undefined;
    const handleKeydown = (event) => {
      if (event.key === "Escape") setSelectedHouseIndex(null);
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [selectedHouseIndex]);
  const selectedWheelInsight = useMemo(() => {
    if (!activeChart || !selectedWheelItem) return null;
    if (selectedWheelItem.type === "planet") {
      const planet = WZ_PLANETS[selectedWheelItem.index];
      const row = planetaryRows.find((item) => item.key === String(planet?.name || "").toLowerCase());
      return {
        title: row?.planetLabel || localizedPlanetName(planet?.name),
        subtitle: row ? `${row.signSymbol} ${row.signLabel} · ${row.degreeInSign.toFixed(2)}°` : "",
        body: row?.description || t("wz_wheel_select_planet_hint"),
      };
    }
    if (selectedWheelItem.type === "sign") {
      const sign = WZ_SIGNS[selectedWheelItem.signIdx];
      return {
        title: localizedSignName(sign?.n),
        subtitle: `${localizedElement(sign?.el)} · ${localizedModality(sign?.mod)}`,
        body: signProfilesByKey[String(sign?.n || "").toLowerCase()]?.strength || t(`wz_sign_strength_${String(sign?.n || "").toLowerCase()}`),
      };
    }
    if (selectedWheelItem.type === "aspect") {
      const aspect = activeChart.aspectList[selectedWheelItem.index];
      if (!aspect) return null;
      const p1 = WZ_PLANETS[aspect.p1];
      const p2 = WZ_PLANETS[aspect.p2];
      const selectedAspectSummary = (planetaryData?.aspect_insights || []).find((item) => {
        const sameAspect = String(item.aspect || "").toLowerCase() === String(aspect.name || "").toLowerCase();
        if (!sameAspect) return false;
        const a = String(item.planet_a || "").toLowerCase();
        const b = String(item.planet_b || "").toLowerCase();
        return a === String(p1?.name || "").toLowerCase() && b === String(p2?.name || "").toLowerCase();
      })?.summary;
      return {
        title: t(`wz_aspect_name_${aspect.name.toLowerCase()}`, aspect.name),
        subtitle: `${localizedPlanetName(p1.name)} · ${localizedPlanetName(p2.name)} · ${interpolate(t("wz_orb_format"), { orb: aspect.orb.toFixed(1) })}`,
        body: selectedAspectSummary || buildAspectInsightSummary({
          planetA: p1.name,
          planetB: p2.name,
          aspectName: aspect.name,
          chartPair: true,
        }),
      };
    }
    return null;
  }, [activeChart, selectedWheelItem, planetaryRows, localizedPlanetName, localizedSignName, localizedElement, localizedModality, buildAspectInsightSummary, signProfilesByKey, planetaryData, t]);
  const selectedAspectFromList = useMemo(() => {
    if (selectedWheelItem?.type !== "aspect") return null;
    return activeChart?.aspectList?.[selectedWheelItem.index] || null;
  }, [selectedWheelItem, activeChart]);
  const selectedAspectInsight = useMemo(() => {
    if (!selectedAspectFromList) return null;
    const selectedPlanetA = String(WZ_PLANETS[selectedAspectFromList.p1]?.name || "").toLowerCase();
    const selectedPlanetB = String(WZ_PLANETS[selectedAspectFromList.p2]?.name || "").toLowerCase();
    const insight = (planetaryData?.aspect_insights || []).find((item) => {
      const sameAspectType = String(item.aspect || "").toLowerCase() === String(selectedAspectFromList.name || "").toLowerCase();
      if (!sameAspectType) return false;
      const insightPlanetA = String(item.planet_a || "").toLowerCase();
      const insightPlanetB = String(item.planet_b || "").toLowerCase();
      return insightPlanetA === selectedPlanetA && insightPlanetB === selectedPlanetB;
    });
    return insight || null;
  }, [planetaryData, selectedAspectFromList]);
  const selectedAspectIsHarmonious = useMemo(() => {
    if (!selectedAspectFromList) return null;
    return [0, 60, 120].includes(Number(selectedAspectFromList.deg));
  }, [selectedAspectFromList]);

  const inputStyle = buildFormInputStyle({ fontSize: fs });

  return (
    <div style={{ padding:32 }}>
      <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:40, fontWeight:400, color:C.textPrimary, marginBottom:8 }}>{t("western_astrology_title")}</h1>
      <p style={{ color:C.textSecondary, fontSize:fs, marginBottom:32 }}>{t("wz_intro_description")}</p>

      <GlassCard style={{ padding:28, marginBottom:36 }}>
        <div className={formFieldGridClassName({ stacked: isMobile })} style={{ ...formFieldGridStyle({ stacked: isMobile }), marginBottom: 20 }}>
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
              <div style={{ fontSize:11, color:acc.primary, letterSpacing:"0.1em" }}>{t("wz_date_of_birth_label")}</div>
              <NativePickerInput
                type="date"
                value={dob}
                onChange={(e) => { setDob(e.target.value); setChart(null); setInputError(""); }}
                disabled={isReplayMode}
                style={inputStyle}
              />
            </div>
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <div style={{ fontSize:11, color:acc.primary, letterSpacing:"0.1em", display:"flex", alignItems:"center", gap:6 }}>
              <span>{t("wz_birth_time_label")} <span style={{color:C.textMuted,fontSize:10}}>({t("wz_optional")})</span></span>
              <div
                style={{ position:"relative", display:"inline-flex" }}
                onMouseEnter={() => setActiveTooltip("birthTime")}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <span
                  style={{ color:C.textMuted, fontSize:12, cursor:"help", lineHeight:1 }}
                  aria-label={t("wz_birth_time_tooltip")}
                >
                  ⓘ
                </span>
                {activeTooltip==="birthTime" && (
                  <div style={{
                    position:"absolute", top:"calc(100% + 8px)", right:0, zIndex:5,
                    width:260, padding:"10px 12px", borderRadius:10,
                    background:"rgba(15, 20, 44, 0.96)", border:"1px solid rgba(255,255,255,0.14)",
                    color:"#e9ecff", fontSize:12, lineHeight:1.45, letterSpacing:"normal", textTransform:"none",
                    boxShadow:"0 10px 30px rgba(0,0,0,0.35)",
                  }}>
                    {t("wz_birth_time_tooltip")}
                  </div>
                )}
              </div>
            </div>
            <NativePickerInput
              type="time"
              value={birthTime}
              onChange={(e) => { setBirthTime(e.target.value); setChart(null); setInputError(""); }}
              disabled={isReplayMode}
              style={inputStyle}
            />
          </div>
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <div style={{ fontSize:11, color:acc.primary, letterSpacing:"0.1em", display:"flex", alignItems:"center", gap:6 }}>
              <span>{t("wz_birthplace_label")} <span style={{color:C.textMuted,fontSize:10}}>({t("wz_optional")})</span></span>
              <div
                style={{ position:"relative", display:"inline-flex" }}
                onMouseEnter={() => setActiveTooltip("birthPlace")}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <span
                  style={{ color:C.textMuted, fontSize:12, cursor:"help", lineHeight:1 }}
                  aria-label={t("wz_birthplace_tooltip")}
                >
                  ⓘ
                </span>
                {activeTooltip==="birthPlace" && (
                  <div style={{
                    position:"absolute", top:"calc(100% + 8px)", right:0, zIndex:5,
                    width:260, padding:"10px 12px", borderRadius:10,
                    background:"rgba(15, 20, 44, 0.96)", border:"1px solid rgba(255,255,255,0.14)",
                    color:"#e9ecff", fontSize:12, lineHeight:1.45, letterSpacing:"normal", textTransform:"none",
                    boxShadow:"0 10px 30px rgba(0,0,0,0.35)",
                  }}>
                    {t("wz_birthplace_tooltip")}
                  </div>
                )}
              </div>
            </div>
            <CityPicker
              value={birthPlace}
              onChange={(value) => { setBirthPlace(value); setChart(null); setInputError(""); }}
              locale={locale}
              acc={acc}
              inputStyle={inputStyle}
              placeholder={t("wz_birthplace_placeholder")}
              maxVisibleItems={4}
              disabled={isReplayMode}
            />
          </div>
        </div>
        {inputError && (
          <div style={{ fontSize: 12, color: "#ffb3b3", marginBottom: 12 }}>
            {inputError}
          </div>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          {!isReplayMode && (
            <Button variant="primary" acc={acc} onClick={generate} disabled={animating || isGenerateLockedByEntitlement} style={{ padding:"12px 36px", fontSize:fs }}>
              {animating ? t("wz_casting_chart") : generateButtonLabel}
            </Button>
          )}
          {chart && hasDailyReadingForExport && !isReplayMode && (
            <div style={{ fontSize:13, color:C.textMuted }}>
              <span style={{ color:acc.primary }}>✦</span> {interpolate(t("wz_credits_used_dynamic"), { credits: westernCreditCost || 2 })}
            </div>
          )}
          {isReplayMode && <div style={{ fontSize:13, color:C.textMuted }}><span style={{color:acc.primary}}>✦</span> {t("dashboard_replay_mode_badge")}</div>}
          {isReplayMode && !loadingReplay && (
            <Button
              variant="primary"
              acc={acc}
              onClick={() => (onResetPage ? onResetPage() : window.location.assign(localizedPath("western", locale)))}
              style={{ padding:"12px 24px", fontSize:fs - 1 }}
            >
              {t("dashboard_replay_start_new_western")}
            </Button>
          )}
          {chart && hasDailyReadingForExport && (
            <Button variant="outline" acc={acc} onClick={exportWesternReadingToPdf}>
              {t("export_result_pdf")}
            </Button>
          )}
        </div>
      </GlassCard>

      {(chart || animating) && (
        <div>
          {animating && (
            <GlassCard style={{ padding:28, marginBottom:24 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:acc.primary, boxShadow:`0 0 12px ${acc.primary}` }} />
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary }}>{t("wz_casting_chart")}</div>
              </div>
              <div style={{ fontSize:13, color:C.textSecondary, marginBottom:14 }}>{t("planetary_loading")}</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:12 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={`loading-card-${i}`}
                    style={{
                      height: i < 3 ? 92 : 72,
                      borderRadius:12,
                      border:"1px solid rgba(255,255,255,0.10)",
                      background:"linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                    }}
                  />
                ))}
              </div>
            </GlassCard>
          )}
          {chart && !animating && (
            <>
              {/* Big 3 */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:28 }}>
                {[
                  {label:t("wz_sun_sign_label"),sign:sun,icon:"☉",desc:t("wz_sun_sign_desc")},
                  {label:t("wz_moon_sign_label"),sign:moon,icon:"☽",desc:t("wz_moon_sign_desc")},
                  {label:t("wz_rising_sign_label"),sign:rising,icon:"↑",desc:t("wz_rising_sign_desc")},
                ].map((item,i) => (
                  <div
                    key={i}
                    onClick={() => openSignModal({
                      index: [activeChart.sunIdx, activeChart.moonIdx, activeChart.risingIdx][i],
                      role: ["sun", "moon", "rising"][i],
                    })}
                    style={{ cursor:"pointer" }}
                  >
                    <GlassCard hoverable style={{ padding:24, textAlign:"center" }} glow glowAlpha={0.45} accent={item.sign?.col}>
                      <div style={{ fontSize:28, color:item.sign?.col, marginBottom:8 }}>{item.icon}</div>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.12em", marginBottom:6 }}>{item.label}</div>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:C.textPrimary, marginBottom:2 }}>{localizedSignName(item.sign?.n)}</div>
                      <div style={{ fontSize:11, color:item.sign?.col }}>{localizedElement(item.sign?.el)} · {localizedModality(item.sign?.mod)}</div>
                      <div style={{ fontSize:12, color:C.textMuted, marginTop:6 }}>{item.desc}</div>
                    </GlassCard>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
                {[
                  ["daily", t("daily_horoscope_reading_title")],
                  ["wheel", t("wz_tab_wheel")],
                  ["planets", t("wz_tab_planets")],
                  ["aspects", t("wz_tab_aspects")],
                  ["houses", t("wz_tab_houses")],
                ].map(([id,label]) => (
                  <button key={id} onClick={() => setActiveTab(id)} style={{
                    padding:"8px 20px", borderRadius:20, border:"none", cursor:"pointer",
                    background: activeTab===id ? `linear-gradient(135deg,${acc.primary},${acc.tertiary})` : "rgba(255,255,255,0.05)",
                    color: activeTab===id ? "#fff" : C.textMuted,
                    fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight: activeTab===id ? 600 : 400,
                    boxShadow: activeTab===id ? `0 0 20px ${acc.primary}44` : "none",
                    transition:"all 0.2s",
                  }}>{label}</button>
                ))}
              </div>

              {/* WHEEL TAB */}
              {activeTab==="wheel" && (
                <div style={{ display:"grid", gridTemplateColumns: isMobilePlanetsView ? "minmax(0,1fr)" : "minmax(300px,400px) 1fr", gap:28, alignItems:"start" }}>
                  <GlassCard style={{ padding:24 }} glow accent={acc.primary}>
                    <NatalWheel chart={activeChart} acc={acc} selectedWheelItem={selectedWheelItem} onSelectWheelItem={setSelectedWheelItem} />
                    <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center", marginTop:16 }}>
                      {WZ_ASPECTS.map((a,i)=>(
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:C.textMuted }}>
                          <div style={{ width:16, height:1.5, background:a.col, borderRadius:1 }}/>{t(`wz_aspect_name_${a.name.toLowerCase()}`, a.name)}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop:12, fontSize:12, color:C.textMuted, textAlign:"center" }}>{t("wz_wheel_interaction_hint")}</div>
                  </GlassCard>
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <GlassCard style={{ padding:28 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary, marginBottom:12 }}>{t("wz_chart_overview_title")}</div>
                      <p style={{ fontSize:fs-1, color:C.textSecondary, lineHeight:1.8, marginBottom:14 }}>
                        {chartNarratives.overview || interpolate(
                          t(
                            "wz_chart_overview_summary",
                            "With your Sun in %{sun}, Moon in %{moon}, and %{rising} rising, your chart weaves together %{sun_element} drive, %{moon_element} emotions, and %{rising_element} expression into a singular cosmic signature."
                          ),
                          {
                            sun: localizedSignName(sun?.n),
                            moon: localizedSignName(moon?.n),
                            rising: localizedSignName(rising?.n),
                            sun_element: localizedElement(sun?.el),
                            moon_element: localizedElement(moon?.el),
                            rising_element: localizedElement(rising?.el),
                          }
                        )}
                      </p>
                      <p style={{ fontSize:fs-1, color:C.textSecondary, lineHeight:1.8 }}>
                        {chartNarratives.ascendant || interpolate(
                          t(
                            "wz_chart_overview_ascendant",
                            "Your %{rising} Ascendant is the mask you present to the world — %{mode} energy that meets life with %{style}."
                          ),
                          {
                            rising: localizedSignName(rising?.n),
                            mode: localizedModality(rising?.mod),
                            style: rising?.el === "Fire"
                              ? t("wz_ascendant_style_fire")
                              : rising?.el === "Earth"
                                ? t("wz_ascendant_style_earth")
                                : rising?.el === "Air"
                                  ? t("wz_ascendant_style_air")
                                  : t("wz_ascendant_style_water"),
                          }
                        )}
                      </p>
                    </GlassCard>
                    <GlassCard style={{ padding:20 }}>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:8 }}>{t("wz_interpretation_helper_title")}</div>
                      {selectedWheelInsight ? (
                        <>
                          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary }}>{selectedWheelInsight.title}</div>
                          <div style={{ fontSize:12, color:acc.primary, marginTop:4 }}>{selectedWheelInsight.subtitle}</div>
                          <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.7, marginTop:10 }}>{selectedWheelInsight.body}</p>
                        </>
                      ) : (
                        <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.7 }}>{t("wz_wheel_select_hint")}</p>
                      )}
                    </GlassCard>
                    <GlassCard style={{ padding:24 }}>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.12em", marginBottom:16 }}>{t("wz_elemental_balance")}</div>
                      {["Fire","Earth","Air","Water"].map(el => {
                        const count = PLANET_SIGN.filter(s=>s.el===el).length;
                        const col = {Fire:"#ff7675",Earth:"#a8e063",Air:"#f7d794",Water:"#74b9ff"}[el];
                        return (
                          <div key={el} style={{ marginBottom:12 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:C.textSecondary, marginBottom:4 }}>
                              <span style={{color:col}}>{localizedElement(el)}</span><span>{interpolate(t("wz_planet_count_format"), { count })}</span>
                            </div>
                            <div style={{ height:6, borderRadius:3, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}>
                              <div style={{ width:`${(count/9)*100}%`, height:"100%", borderRadius:3, background:col, boxShadow:`0 0 8px ${col}88`, transition:"width 1s ease" }}/>
                            </div>
                          </div>
                        );
                      })}
                    </GlassCard>
                  </div>
                </div>
              )}

              {/* DAILY TAB */}
              {activeTab==="daily" && (
                <GlassCard style={{ padding:28 }}>
                  <div style={{
                    padding:"16px 18px",
                    borderRadius:14,
                    border:`1px solid ${dailyMoodStyle.edge}`,
                    background: dailyMoodStyle.gradient,
                    marginBottom:14,
                  }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
                    <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.11em", marginBottom:6 }}>{t("western_astrology_title")}</div>
                      <div style={{ fontSize:11, padding:"4px 10px", borderRadius:999, background:dailyMoodStyle.chipBg, border:`1px solid ${dailyMoodStyle.chipBorder}`, color:dailyMoodStyle.chipText }}>
                        {t(`dashboard_daily_tone_${dailyMood}`, dailyMood)}
                      </div>
                    </div>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:C.textPrimary, marginBottom:6 }}>
                      {t("daily_horoscope_reading_title")}
                    </div>
                    <div style={{ fontSize:12, color:C.textSecondary, lineHeight:1.6, marginBottom:6 }}>
                      {t("wz_daily_today_note")}
                    </div>
                    <div style={{ fontSize:12, color:C.textSecondary, lineHeight:1.6 }}>
                      {t("wz_aspect_guardrail")}
                    </div>
                  </div>
                  {dailyLoading && <div style={{ fontSize:12, color:C.textMuted }}>{t("planetary_loading")}</div>}
                  {dailyError && <div style={{ fontSize:12, color:"#ffb3b3", marginBottom:10 }}>{dailyError}</div>}
                  {!dailyLoading && dailyReading && (
                    <div style={{ display:"grid", gap:12 }}>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:10 }}>
                        {[
                          ["dashboard_daily_lucky_number_label", dailyReading.lucky_number],
                          ["dashboard_daily_power_color_label", localizedPowerColor],
                          ["dashboard_daily_peak_hour_label", dailyReading.peak_hour],
                          ["pdf_energy_index_label", dailyEnergyIndex !== null ? `${dailyEnergyIndex}%` : "—"],
                        ].map(([key, value]) => (
                          <GlassCard key={key} style={{ padding:14, border:`1px solid ${dailyMoodStyle.edge}`, background:"rgba(255,255,255,0.03)" }}>
                            <div style={{ fontSize:10, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t(key)}</div>
                            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, color:dailyMoodStyle.chipText, lineHeight:1 }}>{value}</div>
                          </GlassCard>
                        ))}
                      </div>
                      {!!dailyReading?.peak_hour_note && (
                        <div style={{ marginTop:-4 }}>
                          <div style={{ fontSize:12, color:C.textSecondary }}>{dailyReading.peak_hour_note}</div>
                          <div style={{ fontSize:11, color:C.textMuted, marginTop:4 }}>{t("dashboard_daily_peak_hour_clarity")}</div>
                        </div>
                      )}
                      <GlassCard style={{ padding:18, border:`1px solid ${dailyMoodStyle.edge}`, background:"rgba(255,255,255,0.04)" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:dailyMoodStyle.chipText, boxShadow:`0 0 10px ${dailyMoodStyle.chipText}` }} />
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em" }}>{t("dashboard_daily_overall_label")}</div>
                        </div>
                        {!!dailyReading.today_thread && (
                          <div style={{ fontSize:13, color:dailyMoodStyle.chipText, lineHeight:1.6, marginBottom:10 }}>
                            <span style={{ fontSize:10, letterSpacing:"0.08em", color:C.textMuted, display:"block", marginBottom:4 }}>{t("dashboard_daily_thread_label")}</span>
                            {dailyReading.today_thread}
                          </div>
                        )}
                        <div style={{ fontSize:14, color:C.textPrimary, lineHeight:1.7 }}>{dailyReading.overall_energy}</div>
                        <div style={{ marginTop:10, fontSize:13, color:C.textSecondary, lineHeight:1.7 }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:4 }}>
                            {t("dashboard_daily_reading_label")} · {new Date(`${normalizeIsoDate(readingDate) || normalizeIsoDate(dailyReading.date)}T12:00:00`).toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", { month: "long", day: "numeric", year: "numeric" })}
                          </div>
                          {dailyReading.daily_reading}
                        </div>
                      </GlassCard>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:10 }}>
                        {[
                          ["dashboard_daily_love_relationships_label", dailyReading.love_relationships || dailyReading.love],
                          ["dashboard_daily_career_purpose_label", dailyReading.career_purpose || dailyReading.career],
                          ["dashboard_daily_wellness_body_label", dailyReading.wellness_body || dailyReading.wellbeing],
                          ["dashboard_daily_focus_tip_label", dailyReading.focus_tip],
                        ].map(([labelKey, value]) => (
                          <GlassCard key={labelKey} style={{ padding:14, border:`1px solid ${dailyMoodStyle.edge}`, background:"rgba(255,255,255,0.03)" }}>
                            <div style={{ fontSize:11, color:dailyMoodStyle.chipText, letterSpacing:"0.08em", marginBottom:6 }}>{t(labelKey)}</div>
                            <div style={{ fontSize:13, color:C.textSecondary, lineHeight:1.6 }}>{value}</div>
                          </GlassCard>
                        ))}
                      </div>
                      <GlassCard style={{ padding:16, border:"1px solid rgba(255,255,255,0.12)" }}>
                        <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:6 }}>{t("dashboard_daily_do_today_label")}</div>
                        <div style={{ fontSize:13, color:C.textSecondary, lineHeight:1.6 }}>{dailyReading.do_today}</div>
                        <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginTop:10, marginBottom:6 }}>{t("dashboard_daily_avoid_today_label")}</div>
                        <div style={{ fontSize:13, color:C.textSecondary, lineHeight:1.6 }}>{dailyReading.avoid_today}</div>
                      </GlassCard>
                      {!!dailyReading?.key_aspects?.length && (
                        <GlassCard style={{ padding:16, border:`1px solid ${dailyMoodStyle.edge}` }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("dashboard_daily_key_aspects_label")}</div>
                          <div style={{ display:"grid", gap:8 }}>
                            {dailyReading.key_aspects.map((item, index) => (
                              <div key={`daily-aspect-${index}`} style={{ fontSize:12, color:C.textSecondary, padding:"8px 10px", borderRadius:10, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.08)" }}>
                                {localizedPlanetName(item.planet_a)} · {t(`wz_aspect_name_${String(item.aspect || "").toLowerCase()}`, item.aspect)} · {localizedPlanetName(item.planet_b)} ({Number(item.orb || 0).toFixed(1)}°)
                              </div>
                            ))}
                          </div>
                        </GlassCard>
                      )}
                      {!!dailyReading?.planetary_influences?.length && (
                        <GlassCard style={{ padding:16, border:`1px solid ${dailyMoodStyle.edge}` }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("dashboard_daily_planetary_influences_label")}</div>
                          <div style={{ display:"grid", gap:8 }}>
                            {dailyReading.planetary_influences.map((item, index) => (
                              <div key={`daily-influence-${index}`} style={{ padding:"8px 10px", borderRadius:10, border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.02)" }}>
                                <div style={{ fontSize:12, color:C.textPrimary }}>{item.headline}</div>
                                <div style={{ fontSize:12, color:C.textSecondary, marginTop:3 }}>{item.note}</div>
                              </div>
                            ))}
                          </div>
                        </GlassCard>
                      )}
                      {!!dailyReading?.harmonic_signs?.length && (
                        <GlassCard style={{ padding:16, border:`1px solid ${dailyMoodStyle.edge}` }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("dashboard_daily_harmonic_signs_label")}</div>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            {dailyReading.harmonic_signs.map((sign) => (
                              <span key={`harmonic-${sign}`} style={{ fontSize:12, color:dailyMoodStyle.chipText, border:`1px solid ${dailyMoodStyle.chipBorder}`, background:dailyMoodStyle.chipBg, borderRadius:999, padding:"4px 10px" }}>
                                {t(`wz_sign_${String(sign).toLowerCase()}`)}
                              </span>
                            ))}
                          </div>
                        </GlassCard>
                      )}
                      {!!dailyReading?.affirmation && (
                        <GlassCard style={{ padding:18, border:`1px solid ${dailyMoodStyle.edge}`, background:"rgba(255,255,255,0.04)" }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("dashboard_daily_affirmation_label")}</div>
                          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary, lineHeight:1.4 }}>
                            "{dailyReading.affirmation}"
                          </div>
                        </GlassCard>
                      )}
                      {!!dailyReading?.confidence_notes?.length && (
                        <GlassCard style={{ padding:16 }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("wz_confidence_notes_title")}</div>
                          <div style={{ display:"grid", gap:6 }}>
                            {dailyReading.confidence_notes.map((note, noteIndex) => (
                              <div key={`daily-note-${noteIndex}`} style={{ fontSize:12, color:C.textSecondary }}>• {note}</div>
                            ))}
                          </div>
                        </GlassCard>
                      )}
                    </div>
                  )}
                </GlassCard>
              )}

              {/* PLANETS TAB */}
              {activeTab==="planets" && (
                <div style={{ display:"grid", gridTemplateColumns:isMobilePlanetsView ? "minmax(0,1fr)" : "minmax(0, 1.25fr) minmax(260px, 0.85fr)", gap:16, alignItems:"start" }}>
                  <GlassCard style={{ padding:28 }}>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary, marginBottom:20 }}>{t("wz_planetary_positions_title")}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {planetaryLoading && (
                      <div style={{ fontSize:12, color:C.textMuted }}>{t("planetary_loading")}</div>
                    )}
                    {planetaryError && (
                      <div style={{ fontSize:12, color:"#ffb3b3", marginBottom:4 }}>{planetaryError}</div>
                    )}
                    {planetaryData?.meta && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:6 }}>
                        <div style={{ fontSize:11, padding:"4px 8px", borderRadius:999, border:"1px solid rgba(255,255,255,0.14)", color:C.textSecondary }}>
                          {interpolate(t("planetary_source_badge"), { source: planetarySourceLabel })}
                        </div>
                        <div style={{ fontSize:11, padding:"4px 8px", borderRadius:999, border:"1px solid rgba(255,255,255,0.14)", color:C.textSecondary }}>
                          {interpolate(t("planetary_confidence_badge"), { confidence: Math.round((planetaryData.meta.confidence || 0) * 100) })}
                        </div>
                        {planetaryData.meta.degraded_reason && (
                          <div style={{ fontSize:11, padding:"4px 8px", borderRadius:999, border:"1px solid #ff767555", color:"#ff9e9e" }}>
                            {interpolate(t("planetary_degraded_badge"), { reason: planetaryReasonLabel })}
                          </div>
                        )}
                      </div>
                    )}
                    {!!planetaryData?.confidence_notes?.length && (
                      <div style={{ padding:"10px 12px", borderRadius:10, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.03)" }}>
                        <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t("wz_confidence_notes_title")}</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          {planetaryData.confidence_notes.map((note, index) => (
                            <div key={index} style={{ fontSize:12, color:C.textSecondary }}>• {note}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {planetaryRows.map((row) => {
                      const text = row.description || "";
                      const selected = selectedPlanetRow?.key === row.key;
                      return (
                        <button
                          key={row.key}
                          type="button"
                          onClick={() => {
                            setSelectedPlanetKey(row.key);
                            const planetIdx = WZ_PLANETS.findIndex((planet) => planet.name.toLowerCase() === row.key);
                            if (planetIdx >= 0) setSelectedWheelItem({ type: "planet", index: planetIdx });
                            if (isMobilePlanetsView) setShowPlanetDetailsMobile(true);
                          }}
                          style={{
                            width:"100%",
                            display:"grid",
                            gridTemplateColumns:"44px 1fr auto auto",
                            alignItems:"center",
                            gap:16,
                            padding:"14px 18px",
                            borderRadius:12,
                            background:selected ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
                            border:`1px solid ${selected ? `${row.color}66` : "rgba(255,255,255,0.06)"}`,
                            textAlign:"left",
                            cursor:"pointer",
                          }}
                          aria-pressed={selected}
                        >
                          <div style={{ width:36, height:36, borderRadius:"50%", background:`${row.color}20`, border:`1px solid ${row.color}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:row.color }}>{row.symbol}</div>
                          <div>
                            <div style={{ fontSize:14, color:C.textPrimary, fontWeight:500 }}>{row.planetLabel}</div>
                            <div style={{ fontSize:12, color:C.textMuted, marginTop:2, lineHeight:1.5 }}>
                              {text}
                            </div>
                            {row.retrograde && (
                              <div style={{ marginTop:6, fontSize:11, color:"#ffd18a" }}>{t("planetary_retrograde_chip")}</div>
                            )}
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:16, color:row.color }}>{row.signSymbol} {row.signLabel}</div>
                            <div style={{ fontSize:11, color:C.textMuted }}>{row.degreeInSign.toFixed(2)}° {row.signLabel}</div>
                          </div>
                          <div style={{ padding:"3px 10px", borderRadius:8, background:`${row.color}18`, border:`1px solid ${row.color}44`, fontSize:11, color:row.color, whiteSpace:"nowrap" }}>{row.elementLabel}</div>
                        </button>
                      );
                    })}
                    </div>
                  </GlassCard>
                  {!isMobilePlanetsView && (
                    <GlassCard style={{ padding:20, position:"sticky", top:24 }}>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>{t("wz_planet_details_title")}</div>
                      {selectedPlanetRow ? (
                        <>
                          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary }}>{selectedPlanetRow.planetLabel}</div>
                          <div style={{ fontSize:12, color:selectedPlanetRow.color, marginTop:4 }}>
                            {selectedPlanetRow.signSymbol} {selectedPlanetRow.signLabel} · {selectedPlanetRow.degreeInSign.toFixed(2)}°
                          </div>
                          <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:8 }}>
                            <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_fact_element")}: {selectedPlanetRow.elementLabel}</div>
                            <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_fact_motion")}: {planetSupportContent?.motionLabel}</div>
                          </div>
                          <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.65, marginTop:10 }}>{selectedPlanetRow.description}</p>
                          <div style={{ marginTop:12, display:"grid", gap:8 }}>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_planet_strengths_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{planetSupportContent?.strengths}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_planet_challenges_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{planetSupportContent?.challenges}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_planet_action_tip_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{planetSupportContent?.actionTip}</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p style={{ fontSize:12, color:C.textSecondary, lineHeight:1.6 }}>{t("wz_wheel_select_hint")}</p>
                      )}
                    </GlassCard>
                  )}
                </div>
              )}

              {/* ASPECTS TAB */}
              {activeTab==="aspects" && (
                <GlassCard style={{ padding:28 }}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:C.textPrimary, marginBottom:20 }}>{t("wz_planetary_aspects_title")}</div>
                  <div style={{ marginBottom:14, padding:"10px 12px", borderRadius:12, border:"1px solid rgba(255,255,255,0.12)", background:"linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                      <div style={{ fontSize:12, color:C.textSecondary, lineHeight:1.5 }}>{t("wz_aspects_intro_body")}</div>
                      <button
                        type="button"
                        onClick={() => setShowAspectsGuide((prev) => !prev)}
                        style={{ background:"transparent", border:"none", color:acc.primary, cursor:"pointer", padding:0, fontSize:12, fontWeight:600, whiteSpace:"nowrap" }}
                      >
                        {showAspectsGuide ? t("wz_glossary_cta_hide") : t("wz_glossary_cta_show")}
                      </button>
                    </div>
                    {!showAspectsGuide && (
                      <div style={{ marginTop:8, display:"flex", gap:6, flexWrap:"wrap" }}>
                        <span style={{ fontSize:11, padding:"3px 8px", borderRadius:999, border:"1px solid rgba(255,255,255,0.12)", color:C.textMuted }}>{t("wz_glossary_orb")}</span>
                        <span style={{ fontSize:11, padding:"3px 8px", borderRadius:999, border:"1px solid rgba(255,255,255,0.12)", color:C.textMuted }}>{t("wz_aspect_harmonious")}</span>
                        <span style={{ fontSize:11, padding:"3px 8px", borderRadius:999, border:"1px solid rgba(255,255,255,0.12)", color:C.textMuted }}>{t("wz_aspect_challenging")}</span>
                      </div>
                    )}
                    {showAspectsGuide && (
                      <div style={{ marginTop:8 }}>
                        <div style={{ fontSize:11, color:C.textMuted, marginBottom:8 }}>{t("wz_aspect_guardrail")}</div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                          <div style={{ fontSize:11, padding:"4px 9px", borderRadius:999, border:"1px solid rgba(255,255,255,0.16)", color:C.textSecondary }}>{t("wz_orb_explainer_short")}</div>
                          <div style={{ fontSize:11, padding:"4px 9px", borderRadius:999, border:"1px solid rgba(255,255,255,0.16)", color:C.textSecondary }}>{t("wz_harmonious_explainer")}</div>
                          <div style={{ fontSize:11, padding:"4px 9px", borderRadius:999, border:"1px solid rgba(255,255,255,0.16)", color:C.textSecondary }}>{t("wz_challenging_explainer")}</div>
                        </div>
                        <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:8 }}>
                          <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:6 }}>{t("wz_glossary_title")}</div>
                          <div style={{ display:"grid", gap:6 }}>
                            {["aspect", "orb", "sun", "moon", "rising", "house"].map((term) => (
                              <div key={`guide-${term}`} style={{ fontSize:12, color:C.textSecondary }}>{t(`wz_glossary_${term}`)}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {activeChart.aspectList.length === 0 && <p style={{color:C.textMuted,fontSize:fs-1}}>{t("wz_no_major_aspects")}</p>}
                  <div style={{ display:"grid", gridTemplateColumns:"minmax(0, 1.35fr) minmax(260px, 0.9fr)", gap:16, alignItems:"start" }}>
                    <div>
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        {activeChart.aspectList.map((a,i) => {
                          const p1=WZ_PLANETS[a.p1], p2=WZ_PLANETS[a.p2];
                          const harmonious = [0,60,120].includes(a.deg);
                          const isSelected = selectedWheelItem?.type === "aspect" && selectedWheelItem?.index === i;
                          const aspDesc = {
                            Conjunction: interpolate(t("wz_aspect_desc_conjunction"), { p1: localizedPlanetName(p1.name), p2: localizedPlanetName(p2.name) }),
                            Sextile: interpolate(t("wz_aspect_desc_sextile"), { p1: localizedPlanetName(p1.name), p2: localizedPlanetName(p2.name) }),
                            Square: interpolate(t("wz_aspect_desc_square"), { p1: localizedPlanetName(p1.name), p2: localizedPlanetName(p2.name) }),
                            Trine: interpolate(t("wz_aspect_desc_trine"), { p1: localizedPlanetName(p1.name), p2: localizedPlanetName(p2.name) }),
                            Opposition: interpolate(t("wz_aspect_desc_opposition"), { p1: localizedPlanetName(p1.name), p2: localizedPlanetName(p2.name) }),
                          }[a.name] || "";
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setSelectedWheelItem({ type:"aspect", index:i })}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  setSelectedWheelItem({ type:"aspect", index:i });
                                }
                              }}
                              style={{
                                width:"100%",
                                textAlign:"left",
                                padding:"16px 20px",
                                borderRadius:12,
                                background:isSelected ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.02)",
                                border:`1px solid ${isSelected ? `${a.col}77` : "rgba(255,255,255,0.14)"}`,
                                boxShadow:isSelected ? `0 0 0 1px ${a.col}55 inset` : "none",
                                cursor:"pointer",
                              }}
                              aria-pressed={isSelected}
                            >
                              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8, flexWrap:"wrap" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                  <span style={{fontSize:20,color:p1.col}}>{p1.sym}</span>
                                  <span style={{fontSize:16,color:a.col}}>{a.sym}</span>
                                  <span style={{fontSize:20,color:p2.col}}>{p2.sym}</span>
                                </div>
                                <div>
                                  <span style={{fontSize:14,color:a.col,fontWeight:600}}>{t(`wz_aspect_name_${a.name.toLowerCase()}`, a.name)}</span>
                                  <span style={{fontSize:12,color:C.textMuted,marginLeft:8}}>{localizedPlanetName(p1.name)} — {localizedPlanetName(p2.name)} · {interpolate(t("wz_orb_format"), { orb: a.orb.toFixed(1) })}</span>
                                </div>
                                <div style={{ marginLeft:"auto", fontSize:11, padding:"3px 10px", borderRadius:8, background:harmonious?`${acc.secondary}20`:`${acc.primary}20`, border:`1px solid ${harmonious?`${acc.secondary}66`:`${acc.primary}66`}`, color:harmonious?acc.secondary:acc.primary }}>
                                  {harmonious ? t("wz_aspect_harmonious") : t("wz_aspect_challenging")}
                                </div>
                              </div>
                              <p style={{fontSize:fs-2,color:C.textSecondary,lineHeight:1.65,paddingLeft:4}}>{aspDesc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <GlassCard style={{ padding:16, position:"sticky", top:24 }}>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:8 }}>{t("wz_selected_aspect_title")}</div>
                      {selectedAspectFromList ? (
                        <>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                            <span style={{ fontSize:12, color:C.textSecondary }}>
                              {localizedPlanetName(WZ_PLANETS[selectedAspectFromList.p1].name)} — {localizedPlanetName(WZ_PLANETS[selectedAspectFromList.p2].name)}
                            </span>
                            <span style={{ fontSize:11, color:C.textMuted }}>
                              {interpolate(t("wz_orb_format"), { orb: selectedAspectFromList.orb.toFixed(1) })}
                            </span>
                            <span style={{ marginLeft:"auto", fontSize:11, padding:"3px 10px", borderRadius:8, background:selectedAspectIsHarmonious?`${acc.secondary}20`:`${acc.primary}20`, border:`1px solid ${selectedAspectIsHarmonious?`${acc.secondary}66`:`${acc.primary}66`}`, color:selectedAspectIsHarmonious?acc.secondary:acc.primary }}>
                              {selectedAspectIsHarmonious ? t("wz_aspect_harmonious") : t("wz_aspect_challenging")}
                            </span>
                          </div>
                          <div style={{ fontSize:13, color:C.textPrimary, lineHeight:1.65 }}>
                            {selectedAspectInsight?.summary || buildAspectInsightSummary({
                              planetA: WZ_PLANETS[selectedAspectFromList.p1].name,
                              planetB: WZ_PLANETS[selectedAspectFromList.p2].name,
                              aspectName: selectedAspectFromList.name,
                              chartPair: true,
                            })}
                          </div>
                          <div style={{ fontSize:12, color:C.textSecondary, marginTop:8 }}>
                            {selectedAspectInsight?.practical_tip || t("wz_aspect_tip_default")}
                          </div>
                          <div style={{ fontSize:11, color:C.textMuted, marginTop:8 }}>
                            {t("wz_orb_explainer_short")} · {interpolate(t("wz_orb_format"), { orb: selectedAspectFromList.orb.toFixed(1) })}
                          </div>
                        </>
                      ) : (
                        <p style={{ fontSize:12, color:C.textSecondary, lineHeight:1.6 }}>{t("wz_wheel_select_hint")}</p>
                      )}
                    </GlassCard>
                  </div>
                </GlassCard>
              )}

              {/* HOUSES TAB */}
              {activeTab==="houses" && (
                <div style={{ display:"grid", gridTemplateColumns:"minmax(280px,1fr) minmax(320px,0.9fr)", gap:16, alignItems:"start" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:14 }}>
                    {houseRows.map((house, index) => (
                      <button
                        key={house.houseNumber}
                        type="button"
                        onClick={() => setSelectedHouseIndex(index)}
                        style={{ textAlign:"left", border:"none", background:"transparent", padding:0, cursor:"pointer" }}
                      >
                        <GlassCard hoverable style={{ padding:20, border:selectedHouseIndex===index ? `1px solid ${house.sign.col}66` : undefined }} accent={house.sign.col}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                            <div style={{ fontSize:12, color:house.sign.col, fontWeight:600, letterSpacing:"0.08em" }}>{interpolate(t("wz_house_label"), { house: WZ_HOUSES[index] || house.houseNumber })}</div>
                            <div style={{ fontSize:20, color:house.sign.col }}>{house.sign.s}</div>
                          </div>
                          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:C.textPrimary, marginBottom:3 }}>{house.theme}</div>
                          <div style={{ fontSize:12, color:C.textMuted, marginBottom:8 }}>{localizedSignName(house.sign.n)} · {localizedElement(house.sign.el)} · {localizedModality(house.sign.mod)}</div>
                          <div style={{ fontSize:11, color:C.textMuted, marginBottom:10 }}>
                            {t("wz_house_cusp_label")} {house.cuspDegree.toFixed(2)}° {localizedSignName(house.sign.n)}
                          </div>
                          {house.planets.length > 0 && (
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                              {house.planets.map((planet, planetIndex) => (
                                <div key={`${house.houseNumber}-${planetIndex}`} title={planet.name} style={{ width:26, height:26, borderRadius:"50%", background:`${planet.color}20`, border:`1px solid ${planet.color}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:planet.color }}>{planet.symbol}</div>
                              ))}
                            </div>
                          )}
                        </GlassCard>
                      </button>
                    ))}
                  </div>
                  <GlassCard style={{ padding:20, position:"sticky", top:24 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                      <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_houses_drawer_title")}</div>
                      <div style={{ fontSize:11, color:C.textMuted }}>{interpolate(t("wz_house_source_label"), { system: houseSystemLabel, source: houseSourceLabel })}</div>
                    </div>
                    {(String(planetaryData?.chart_primitives?.house_source_mode || "") !== "swiss_houses" || houseSourceReasonLabel) && (
                      <div style={{ marginBottom:10, padding:"8px 10px", borderRadius:10, border:"1px solid rgba(255,167,122,0.4)", background:"rgba(255,167,122,0.12)" }}>
                        <div style={{ fontSize:12, color:"#ffd3bd" }}>
                          {interpolate(t("wz_house_fallback_banner"), { source: houseSourceLabel })}
                        </div>
                        {houseSourceReasonLabel && (
                          <div style={{ fontSize:11, color:"#ffb08a", marginTop:3 }}>
                            {interpolate(t("wz_house_fallback_reason"), { reason: houseSourceReasonLabel })}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedHouse ? (
                      <>
                        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, color:C.textPrimary }}>{interpolate(t("wz_house_drawer_heading"), { house: selectedHouse.houseNumber })}</div>
                        <div style={{ fontSize:13, color:selectedHouse.sign.col, marginTop:4 }}>{localizedSignName(selectedHouse.sign.n)} · {selectedHouse.cuspDegree.toFixed(2)}°</div>
                        <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.65, marginTop:10 }}>{interpolate(t("wz_house_interp_template"), {
                          house_theme: selectedHouse.theme,
                          sign: localizedSignName(selectedHouse.sign.n),
                          element: localizedElement(selectedHouse.sign.el),
                          modality: localizedModality(selectedHouse.sign.mod).toLowerCase(),
                        })}</p>
                        {selectedHouseInsights && (
                          <div style={{ marginTop:10, display:"grid", gap:8 }}>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_house_insight_theme_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{selectedHouseInsights.theme}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_house_insight_expression_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{selectedHouseInsights.expression}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em" }}>{t("wz_house_insight_growth_title")}</div>
                              <div style={{ fontSize:12, color:C.textSecondary }}>{selectedHouseInsights.growth}</div>
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginTop:10 }}>{t("wz_house_planets_in_house")}</div>
                        {selectedHouse.planets.length ? (
                          <div style={{ display:"grid", gap:8, marginTop:8 }}>
                            {selectedHouse.planets.map((planet, planetIndex) => (
                              <div key={`drawer-${planetIndex}`} style={{ padding:"8px 10px", borderRadius:10, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.02)" }}>
                                <div style={{ fontSize:13, color:C.textPrimary }}>{planet.symbol} {planet.name}</div>
                                <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{planet.degreeInSign.toFixed(2)}° {planet.signLabel}{planet.retrograde ? ` · ${t("planetary_retrograde_chip")}` : ""}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize:12, color:C.textMuted, marginTop:8 }}>{t("wz_house_no_planets")}</div>
                        )}
                      </>
                    ) : (
                      <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.65 }}>{t("wz_house_drawer_hint")}</p>
                    )}
                    {!!planetaryData?.confidence_notes?.length && (
                      <div style={{ marginTop:14 }}>
                        <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.08em", marginBottom:6 }}>{t("wz_confidence_notes_title")}</div>
                        <div style={{ display:"grid", gap:4 }}>
                          {planetaryData.confidence_notes.map((note, noteIndex) => (
                            <div key={`house-note-${noteIndex}`} style={{ fontSize:12, color:C.textSecondary }}>• {note}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </GlassCard>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!chart && (
        <>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:400, color:C.textPrimary, marginBottom:20 }}>{t("wz_all_signs_title")}</h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:14 }}>
            {WZ_SIGNS.map((z,i) => {
              const isHovered = canHover && hoveredAllSignIndex === i;
              return (
              <div
                key={i}
                onClick={() => openSignModal({ index: i, role: null })}
                onMouseEnter={canHover ? () => setHoveredAllSignIndex(i) : undefined}
                onMouseLeave={canHover ? () => setHoveredAllSignIndex(null) : undefined}
                style={{
                  cursor:"pointer",
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                  transition: "transform 220ms ease",
                }}
              >
                <GlassCard
                  hoverable
                  style={{
                    padding:18,
                    textAlign:"center",
                    background: isHovered ? `${z.col}0d` : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isHovered ? `${z.col}33` : "rgba(255,255,255,0.07)"}`,
                    boxShadow: isHovered ? `0 0 14px ${z.col}22` : "none",
                    transition: "background 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
                  }}
                  accent={z.col}
                >
                  <div style={{ fontSize:32, marginBottom:8, color:z.col }}>{z.s}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:C.textPrimary, marginBottom:3 }}>{localizedSignName(z.n)}</div>
                  <div style={{ fontSize:11, color:z.col }}>{localizedElement(z.el)} · {localizedModality(z.mod)}</div>
                </GlassCard>
              </div>
            );})}
          </div>
          <div style={{ marginTop:12, fontSize:12, color:C.textMuted, textAlign:"center" }}>
            {t("wz_all_signs_hint")}
          </div>
        </>
      )}

      {selectedSignModal !== null && (() => {
        const sign = WZ_SIGNS[selectedSignModal.index];
        const signProfile = resolveWesternSignProfile(sign.n, signProfilesByKey, t);
        const compatible = ELEMENT_COMPAT[sign.el] || [];
        const roleContent = selectedSignModal.role ? bigThreePopupContentByRole[selectedSignModal.role] : null;
        return (
          <div
            onClick={closeSignModal}
            style={{
              position:"fixed", inset:0, zIndex:60,
              background:"rgba(3,6,20,0.72)", backdropFilter:"blur(4px)",
              display:"flex", alignItems:"center", justifyContent:"center", padding:20,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width:"min(760px, 100%)",
                background:"rgba(10,16,38,0.96)",
                border:`1px solid ${sign.col}66`,
                borderRadius:18,
                boxShadow:`0 20px 60px rgba(0,0,0,0.45), 0 0 28px ${sign.col}44`,
                padding:"24px 24px 20px",
              }}
            >
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:42, lineHeight:1, color:sign.col, marginBottom:8 }}>{sign.s}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, color:C.textPrimary, lineHeight:1.1 }}>{localizedSignName(sign.n)}</div>
                  <div style={{ marginTop:8, fontSize:12, color:sign.col, letterSpacing:"0.08em" }}>{localizedElement(sign.el).toUpperCase()} · {localizedModality(sign.mod).toUpperCase()}</div>
                </div>
                <button
                  type="button"
                  onClick={closeSignModal}
                  style={{
                    border:"none", background:"transparent", color:C.textMuted, cursor:"pointer",
                    fontSize:24, lineHeight:1, padding:0,
                  }}
                  aria-label={t("close")}
                >
                  ×
                </button>
              </div>

              {roleContent && (
                <div style={{ marginBottom:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                  <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:8 }}>{roleContent.label}</div>
                  <div style={{ fontSize:14, color:C.textSecondary, lineHeight:1.65 }}>
                    <span style={{ color:sign.col, marginRight:6 }}>{roleContent.icon}</span>
                    {roleContent.summary}
                  </div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
                {signProfile.dateRange ? (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                    <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t("wz_sign_dates")}</div>
                    <div style={{ fontSize:14, color:C.textPrimary }}>{signProfile.dateRange}</div>
                  </div>
                ) : null}
                {signProfile.ruler ? (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                    <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t("wz_sign_ruling_planet")}</div>
                    <div style={{ fontSize:14, color:C.textPrimary }}>{signProfile.ruler}</div>
                  </div>
                ) : null}
                {signProfile.strength ? (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                    <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t("wz_sign_core_strength")}</div>
                    <div style={{ fontSize:14, color:C.textSecondary, lineHeight:1.6 }}>{signProfile.strength}</div>
                  </div>
                ) : null}
                {signProfile.growthEdge ? (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                    <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:6 }}>{t("wz_sign_growth_edge")}</div>
                    <div style={{ fontSize:14, color:C.textSecondary, lineHeight:1.6 }}>{signProfile.growthEdge}</div>
                  </div>
                ) : null}
              </div>
              <div style={{ marginTop:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:14 }}>
                <div style={{ fontSize:11, color:C.textMuted, letterSpacing:"0.1em", marginBottom:10 }}>{t("wz_sign_best_energy_match")}</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {compatible.slice(0, 6).map((name) => (
                    <span key={name} style={{ fontSize:12, color:sign.col, border:`1px solid ${sign.col}55`, background:`${sign.col}1a`, borderRadius:999, padding:"4px 10px" }}>
                      {localizedSignName(name)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <PredictionAccessPopup
        open={showAccessPopup}
        title={accessPopupCopy.title}
        message={accessPopupCopy.message}
        onClose={() => setShowAccessPopup(false)}
        onGoToPricing={() => {
          window.location.href = localizedPath("creditsPlans", locale);
        }}
        acc={acc}
      />
      <FreeQuotaConfirmPopup
        open={showFreeQuotaPopup}
        title={getFreeQuotaPopupCopy("western", t).title}
        message={getFreeQuotaPopupCopy("western", t).message}
        note={getFreeQuotaPopupCopy("western", t).note}
        confirmLabel={t("free_quota_popup_confirm")}
        cancelLabel={t("free_quota_popup_cancel")}
        onClose={() => setShowFreeQuotaPopup(false)}
        onConfirm={() => {
          setShowFreeQuotaPopup(false);
          generate({ skipFreeQuotaGuard: true });
        }}
        acc={acc}
      />
      {isMobilePlanetsView && showPlanetDetailsMobile && selectedPlanetRow && (
        <div
          onClick={() => setShowPlanetDetailsMobile(false)}
          style={{
            position:"fixed",
            inset:0,
            zIndex:65,
            background:"rgba(3,6,20,0.72)",
            backdropFilter:"blur(4px)",
            display:"flex",
            alignItems:"flex-end",
            justifyContent:"center",
            padding:12,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width:"min(680px, 100%)",
              maxHeight:"82vh",
              overflowY:"auto",
              background:"rgba(10,16,38,0.98)",
              border:`1px solid ${selectedPlanetRow.color}66`,
              borderRadius:"16px 16px 12px 12px",
              padding:"16px 16px 18px",
            }}
          >
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:C.textPrimary }}>{selectedPlanetRow.planetLabel}</div>
              <button type="button" onClick={() => setShowPlanetDetailsMobile(false)} style={{ border:"none", background:"transparent", color:C.textMuted, fontSize:24, cursor:"pointer", padding:0 }}>×</button>
            </div>
            <div style={{ fontSize:12, color:selectedPlanetRow.color, marginTop:2 }}>
              {selectedPlanetRow.signSymbol} {selectedPlanetRow.signLabel} · {selectedPlanetRow.degreeInSign.toFixed(2)}°
            </div>
            <p style={{ fontSize:13, color:C.textSecondary, lineHeight:1.65, marginTop:10 }}>{selectedPlanetRow.description}</p>
            <div style={{ marginTop:8, display:"grid", gap:8 }}>
              <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_fact_element")}: {selectedPlanetRow.elementLabel}</div>
              <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_fact_motion")}: {planetSupportContent?.motionLabel}</div>
              <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_strengths_title")}: {planetSupportContent?.strengths}</div>
              <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_challenges_title")}: {planetSupportContent?.challenges}</div>
              <div style={{ fontSize:12, color:C.textSecondary }}>{t("wz_planet_action_tip_title")}: {planetSupportContent?.actionTip}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
